/**
 * Hook générique pour les assessments
 * Remplace useDigitalTransformationAssessment et useTrainingTransformationAssessment
 * pour éviter la duplication de code
 */

import { useAssessmentModal } from './useAssessmentModal';
import { useAssessmentValidation } from './useAssessmentValidation';
import { calculateAssessmentScores, getFilteredQuestions, groupQuestionsByCategory } from '@/utils/assessmentScoring';
import { filterLearningStrategyQuestions } from '@/utils/trainingTransformationUtils';

interface UseGenericAssessmentProps {
  isOpen: boolean;
  startAtFirstQuestion?: boolean;
  assessmentType: 'ia-diagnostic' | 'training-transformation';
  data: any[];
  categoryMapping: any;
}

export const useGenericAssessment = ({
  isOpen,
  startAtFirstQuestion = false,
  assessmentType,
  data,
  categoryMapping,
}: UseGenericAssessmentProps) => {
  const { validateCategoryAnswers, validateContactInfo, getVisibleQuestions } = useAssessmentValidation();

  // Filtrer les questions
  const profileQuestions = data.filter((q) => q.category === 'Profil entreprise');

  // Fonction pour filtrer les questions selon les réponses conditionnelles
  const scoredQuestions = getFilteredQuestions(data, {}, 'Profil entreprise');

  // Grouper les questions par catégorie avec le mapping correct
  const questionsByCategory = groupQuestionsByCategory(scoredQuestions, categoryMapping);

  // Les catégories à afficher (sans Profil entreprise)
  const categories = Object.keys(questionsByCategory).filter((cat) => cat !== 'Profil entreprise');

  // Calcul correct : Intro(0) + Profile(1) + Categories + Contact + Results
  const totalSteps = 2 + categories.length + 2;

  const assessmentModal = useAssessmentModal({
    assessmentType,
    totalSteps,
    startAtFirstQuestion,
    isOpen,
  });

  const calculateScores = () => {
    return calculateAssessmentScores(scoredQuestions, assessmentModal.answers, categories);
  };

  const getFilteredQuestionsForCurrentCategory = () => {
    if (assessmentModal.currentStep >= 2 && assessmentModal.currentStep < 2 + categories.length) {
      const categoryIndex = assessmentModal.currentStep - 2;
      const category = categories[categoryIndex];
      const categoryQuestions = questionsByCategory[category] || [];

      if (assessmentType === 'training-transformation') {
        return filterLearningStrategyQuestions(categoryQuestions, assessmentModal.answers);
      }

      return getVisibleQuestions(categoryQuestions, assessmentModal.answers);
    }
    return [];
  };

  const canProceed = () => {
    if (assessmentModal.currentStep === 0) return true;

    if (assessmentModal.currentStep === 1) {
      return validateCategoryAnswers(profileQuestions, assessmentModal.answers);
    }

    if (assessmentModal.currentStep >= 2 && assessmentModal.currentStep < totalSteps - 2) {
      const categoryQuestions = getFilteredQuestionsForCurrentCategory();
      return validateCategoryAnswers(categoryQuestions, assessmentModal.answers, data);
    }

    if (assessmentModal.currentStep === totalSteps - 2) {
      const isValid = validateContactInfo(assessmentModal.contactInfo);
      console.log('[DIAGNOSTIC] Contact validation:', { isValid });
      return isValid;
    }

    return true;
  };

  const nextStep = async () => {
    if (assessmentModal.currentStep < totalSteps - 1 && canProceed()) {
      await assessmentModal.nextStep(calculateScores, scoredQuestions, categories);
    }
  };

  const forceNextStep = async () => {
    await assessmentModal.nextStep(calculateScores, scoredQuestions, categories);
  };

  return {
    ...assessmentModal,
    profileQuestions,
    scoredQuestions,
    questionsByCategory,
    categories,
    totalSteps,
    calculateScores,
    canProceed,
    nextStep,
    forceNextStep,
    getFilteredQuestionsForCurrentCategory,
  };
};
